
/* eslint-disable jsx-a11y/label-has-associated-control, no-prototype-builtins */
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import withStyles from '@material-ui/core/styles/withStyles'
import { Container, Checkbox } from '@material-ui/core'
import * as Yup from 'yup'
import styles from './View.Style'
import Formbuilder from 'components_newui/formBuilder/View'

/** ---------- Validation ---------- */
const validationSchema = Yup.object().shape({
  templateName: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(100, 'Maximum 100 characters')
    .required('Template Name is required')
    .trim()
    .matches(
      /^[a-zA-Z0-9_ #!?|]*$/,
      'Only letters, numbers, spaces, underscore (_), hash (#), exclamation (!), question mark (?), and pipe (|) are allowed'
    ),
  templateActive: Yup.boolean().required(),
  templateJson: Yup.array().of(Yup.object()).min(1, 'Add at least one item in the template'),
  totalWeight: Yup.number()
    .required('Weight required')
    .test('equals-100', 'Total weight must equal 100', (v) => Number(v) === 100),
})

/** ---------- Helpers ---------- */
const computeTotalWeight = (items = []) =>
  items
    .filter((item) => Object.prototype.hasOwnProperty.call(item, 'weight'))
    .reduce((sum, item) => sum + Number(item.weight || 0), 0)

const CreateScorecardTemplate = (props) => {
  const {
    classes,
    myEmail,
    postScorecardTemplateCreateFn,
    createScorecardTemplateLoading,
    // Optional: for edit-mode seeding if you add later
    initialTemplate, // { templateName, templateActive, templateJson, totalWeight }
  } = props || {}
 const [scorecardJSONRowFormData, setScorecardJSONRowFormData] = useState([])

  /** ---------- Source-of-truth states ---------- */
  const [builderData, setBuilderData] = useState(
    Array.isArray(initialTemplate?.templateJson) ? initialTemplate.templateJson : []
  )

  // Derived total (memoized to avoid recomputing on unrelated renders)
  const totalWeight = useMemo(() => computeTotalWeight(builderData), [builderData])

  // The form payload that is validated and submitted
  const [scorecardTemplateValue, setScorecardTemplateValue] = useState({
    templateName: initialTemplate?.templateName || '',
    templateActive: initialTemplate?.templateActive ?? true,
    totalWeight: Number(initialTemplate?.totalWeight || 0),
    templateJson: Array.isArray(initialTemplate?.templateJson) ? initialTemplate.templateJson : [],
  })

  // Keep payload in sync with builderData & computed total
  useEffect(() => {
    setScorecardTemplateValue((prev) => ({
      ...prev,
      templateJson: scorecardJSONRowFormData,
      totalWeight,
    }))
  }, [builderData, totalWeight])

  /** ---------- Validation ---------- */
  const [errors, setErrors] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)

  useEffect(() => {
    let mounted = true
    const validateForm = async () => {
      try {
        await validationSchema.validate(scorecardTemplateValue, { abortEarly: false })
        if (mounted) {
          setErrors({})
          setIsFormValid(true)
        }
      } catch (err) {
        const formatted = {}
        if (Array.isArray(err?.inner)) {
          err.inner.forEach((e) => {
            if (e?.path && !formatted[e.path]) formatted[e.path] = e.message
          })
        }
        if (mounted) {
          setErrors(formatted)
          setIsFormValid(false)
        }
      }
    }
    validateForm()
    return () => {
      mounted = false
    }
  }, [scorecardTemplateValue])

  /** ---------- Handlers ---------- */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target
    setScorecardTemplateValue((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }, [])

  console.log("setScorecardTemplateValue ==> all params", scorecardTemplateValue)

  const handleSave = useCallback(async () => {
    if (!isFormValid || totalWeight !== 100 || createScorecardTemplateLoading) return
    try {
      await postScorecardTemplateCreateFn(myEmail, scorecardTemplateValue)
      // success toast/snackbar can be placed here
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while saving template:', error)
      alert('Something went wrong. Please try again.')
    }
  }, [isFormValid, totalWeight, createScorecardTemplateLoading, myEmail, scorecardTemplateValue])

  /** ---------- Render ---------- */
  const btnDisabled = !isFormValid || totalWeight !== 100 || !!createScorecardTemplateLoading

  return (
    <Container fullWidth={false}>
      <div className={classes.createscorecardwrap}>
        <div className={classes.headingwrp}>
          <h2 className={classes.title}>Create Scorecard Template</h2>
        </div>

        <div className={classes.formbox}>
          <div className="block">
            <div className={classes.fieldbox}>
              <label htmlFor="templateName" className={classes.labels}>Template Name</label>
              <input
                className={classes.inputs}
                value={scorecardTemplateValue.templateName}
                type="text"
                name="templateName"
                id="templateName"
                onChange={handleChange}
                maxLength={100}
                required
                aria-required
              />
              {errors.templateName && <p className={classes.error}>{errors.templateName}</p>}
            </div>

            {/* <div className={classes.fieldbox}>
              <label htmlFor="templateActive" className={classes.labels}>Make Template Active</label>
              <Checkbox
                id="templateActive"
                checked={!!scorecardTemplateValue.templateActive}
                onChange={handleChange}
                inputProps={{ 'aria-label': 'templateActive' }}
                name="templateActive"
              />
              {errors.templateActive && <p className={classes.error}>{errors.templateActive}</p>}
            </div> */}
          </div>

          <OverAllWeight
            scorecardUpdateListdata={builderData}
            totalWeight={totalWeight}
            classes={classes}
          />
        </div>

        <h3>Create your template</h3>
        <Formbuilder
          isLoading={!!createScorecardTemplateLoading}
          onSave={handleSave}
          btnDisable={btnDisabled}
          /** Even if Formbuilder doesn’t fire this on mount, the parent is already seeded from initialTemplate */
          setScoreCardTemplatebuilderFormData={setBuilderData}
          setScorecardJSONRowFormData={setScorecardJSONRowFormData}
          /** If Formbuilder supports preloading, uncomment: */
          // initialData={scorecardTemplateValue.templateJson}
        />
      </div>
    </Container>
  )
}

export default withStyles(styles)(CreateScorecardTemplate)

/** ---------- Child: OverAllWeight (presentational only) ---------- */
const OverAllWeight = ({ scorecardUpdateListdata, totalWeight, classes }) => {
  const arr = Array.isArray(scorecardUpdateListdata) ? scorecardUpdateListdata : []
  const ok = totalWeight === 100

  return (
    <div className={classes.overallWeight}>
      <p className={classes.labels}>Overall scoring weights</p>
      <div className={classes.overallBox}>
        <div className={classes.overallHeading}>
          <h4 className={classes.heading}>Question</h4>
          <h4 className={classes.heading}>Weight</h4>
        </div>

        {arr.map((item, index) => (
          <div key={item?.id ?? `${item?.label ?? 'row'}-${index}`} className={classes.overallWeightRow}>
            <h4 className={classes.heading}>{item?.label}</h4>
            <p className={classes.heading}>{item?.weight}</p>
          </div>
        ))}

        <div className={classes.overallWeightRow}>
          <h4 className={classes.heading}>Total Weight</h4>
          <p
            className={classes.heading}
            style={{ color: ok ? 'green' : 'red', fontWeight: 'bold' }}
          >
            {totalWeight}
          </p>
        </div>

        {!ok && (
          <p style={{ color: 'red', marginTop: '10px' }}>
            Total weight must equal 100. Current total: {totalWeight}
          </p>
        )}
      </div>
    </div>
  )
}
